/**
 * Refreshing the chat socket's task-context delegation, in place.
 *
 * WHY THIS EXISTS. A chat socket authenticates once at the handshake and then lives as long as the
 * browser tab; the access token it presented lasts fifteen minutes. Chat itself is unaffected — its
 * identity was established at connect and is not re-derived per turn — but task-context capture
 * FORWARDS that assertion on every run, so past minute fifteen every contextual turn takes a
 * guaranteed 401. The gateway makes that a sticky state of the session, tells the client once per
 * expiry via `chatSessionExpired`, and offers `reauthenticate` as the way back.
 *
 * Unhandled, the consequence is not cosmetic: in render mode every contextual turn aborts, for a
 * reason the user cannot see and cannot act on, until they happen to reload.
 *
 * WHY IT IS ITS OWN MODULE. It is the one piece of the chat page with a real state machine —
 * refresh, then re-present, then wait for an acknowledgement that may never come, then interpret
 * it — with four distinct failure modes, one of which is retryable and three of which are not.
 * Extracting it means the test does not have to mount a 2 000-line component, a socket, a router
 * and a Redux store to exercise those branches.
 *
 * Every collaborator is injected for the same reason.
 */

/**
 * The outcome, as a closed set. Named rather than boolean because the failures want different
 * treatment — one is worth retrying and the rest are not — and a caller that cannot tell them
 * apart will pick one and be wrong the rest of the time.
 *
 *   refreshed         the delegation is good again; the conversation continues untouched.
 *   refresh-failed    the refresh cookie is gone or rejected — the session is genuinely over.
 *   rejected          the refresh worked and the GATEWAY still refused the token. Nothing the user
 *                     can do in this tab fixes it, so it must not be retried into the same wall.
 *   no-token          the refresh answered without a token. Same treatment, different cause.
 */
export const DELEGATION_REFRESHED = 'refreshed';
export const DELEGATION_REFRESH_FAILED = 'refresh-failed';
export const DELEGATION_REJECTED = 'rejected';
export const DELEGATION_NO_TOKEN = 'no-token';
/**
 * The gateway never acknowledged. RETRYABLE, and distinct from a refusal for that reason.
 *
 * A Socket.IO acknowledgement is a callback with no deadline of its own: if the socket drops
 * between the emit and the ack, or the ack is simply lost, the promise wrapping it never settles.
 * Nothing else would ever settle it either — the gateway emits `chatSessionExpired` only once per
 * expiry, because the state is sticky — so the UI would sit forever showing neither success nor
 * failure while every contextual turn kept aborting.
 */
export const DELEGATION_PRESENT_TIMEOUT = 'present-timeout';

/** How long to wait for the gateway's acknowledgement. Short: this is a local round-trip. */
export const PRESENT_TIMEOUT_MS = 5000;

/**
 * Refresh the access token and re-present it to the chat gateway.
 *
 * @param {object}   deps
 * @param {function} deps.refresh   `() => Promise<{ accessToken?: string }>` — the app's ordinary
 *                                  refresh call. The refresh cookie is httpOnly, so this is the
 *                                  same endpoint AxiosInstance uses on a 401; it is triggered here
 *                                  by a socket event rather than by a failed request.
 * @param {function} deps.persist   `(accessToken) => void` — put the new token where the socket's
 *                                  auth callback will read it. Not optional in practice: that
 *                                  callback runs on every reconnection, and a stale store would
 *                                  replay the expired token the moment the socket drops.
 * @param {function} deps.present   `(accessToken) => Promise<{ ok: boolean, reason?: string }>` —
 *                                  emit `reauthenticate` and resolve with the acknowledgement.
 * @param {number}   [deps.timeoutMs]  how long to wait for that acknowledgement.
 * @param {function} [deps.wait]     `(ms) => Promise<void>` — the timeout clock, injectable so a
 *                                   test can force or suppress it without real time passing.
 * @param {number}   [deps.presentAttempts]  how many times to PRESENT the token before giving up.
 *                                   The retry lives here rather than in the caller so it re-uses
 *                                   the token already obtained: a lost acknowledgement says
 *                                   nothing about the refresh, and repeating the HTTP round-trip
 *                                   to get a token we are still holding is work for nothing. (It
 *                                   is also the shape that stays correct if the backend ever
 *                                   starts rotating refresh tokens, where a second refresh would
 *                                   invalidate the first.) Bounded, and small: a silent give-up
 *                                   strands the session and an unbounded retry hammers a dead
 *                                   socket.
 * @returns {Promise<{ outcome: string, reason?: string, retryable?: boolean }>} never throws.
 */
export async function refreshChatDelegation({
    refresh,
    persist,
    present,
    timeoutMs = PRESENT_TIMEOUT_MS,
    wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    presentAttempts = 2,
}) {
    let accessToken;
    try {
        const data = await refresh();
        accessToken = data && data.accessToken;
    } catch (err) {
        return { outcome: DELEGATION_REFRESH_FAILED, reason: 'refresh-threw' };
    }
    if (typeof accessToken !== 'string' || accessToken.length === 0) {
        return { outcome: DELEGATION_NO_TOKEN };
    }

    // Persisted BEFORE it is presented, and deliberately so. If the socket drops between the two,
    // the reconnect handshake must use the new token; the reverse order would leave a window in
    // which the gateway has accepted a token the store has never heard of.
    persist(accessToken);

    // A sentinel rather than a rejection, so a genuine `present` rejection stays distinguishable
    // from the deadline expiring.
    const TIMED_OUT = Symbol('present-timeout');

    // Only the PRESENTATION is retried, never the refresh: a lost acknowledgement says nothing
    // about the token, which is already in hand and already persisted.
    for (let attempt = 1; attempt <= Math.max(1, presentAttempts); attempt += 1) {
        let settled;
        try {
            const acknowledgement = Promise.resolve(present(accessToken));
            // The late-acknowledgement case: after the race is decided this promise may still
            // settle, and if it REJECTS with nothing attached it becomes an unhandled rejection.
            // Attaching a sink here is harmless for the winning path (a second handler on a
            // settled promise) and is the only place that knows the promise exists.
            acknowledgement.catch(() => {});
            settled = await Promise.race([acknowledgement, wait(timeoutMs).then(() => TIMED_OUT)]);
        } catch (err) {
            return { outcome: DELEGATION_REJECTED, reason: 'present-threw' };
        }

        if (settled === TIMED_OUT) continue;
        if (settled && settled.ok) return { outcome: DELEGATION_REFRESHED };
        // A REFUSAL ends it. The gateway saw the token and said no, and presenting the same token
        // again would hit the same wall — only a lost acknowledgement is worth another go.
        return { outcome: DELEGATION_REJECTED, reason: (settled && settled.reason) || 'unknown' };
    }

    // Every attempt timed out. Deliberately NOT a refusal: the gateway may have accepted the token
    // and lost the ack, or may never have seen it. A caller that treated this as "refused" would
    // stop trying when trying again is exactly the right move.
    return { outcome: DELEGATION_PRESENT_TIMEOUT, retryable: true };
}
